import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, Truck, ShieldCheck, RotateCcw, Minus, Plus, Flame, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Countdown from "@/components/Countdown";
import { useCart } from "@/context/CartContext";
import { trackViewContent } from "@/lib/pixels";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add, setOpen } = useCart();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      trackViewContent(r.data);
      api.get("/products", { params: { category: r.data.category, limit: 5 } })
        .then((rr) => setRelated(rr.data.filter((x) => x.id !== id).slice(0, 4)));
    }).catch(() => nav("/tienda"));
  }, [id, nav]);

  if (!p) return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-drelo-muted">Cargando...</div>;

  const discount = p.compare_at ? Math.round((1 - p.price / p.compare_at) * 100) : 0;

  const buyNow = () => { add(p, qty); nav("/carrito"); };
  const addCart = () => { add(p, qty); toast.success("Añadido al carrito 🛒"); };

  return (
    <div className="max-w-7xl mx-auto px-3 md:px-6 py-6">
      <Link to="/tienda" className="inline-flex items-center gap-1 text-sm text-drelo-muted mb-4 hover:text-drelo-primary"><ChevronLeft className="w-4 h-4" /> Volver</Link>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="relative rounded-2xl overflow-hidden border bg-white">
          <img src={p.image} alt={p.title} className="w-full aspect-square object-cover" data-testid="product-image" />
          {discount > 0 && <span className="absolute top-4 left-4 bg-drelo-urgency text-white font-black px-3 py-1.5 rounded-lg text-lg">-{discount}%</span>}
        </div>

        <div>
          {p.badge && <span className="inline-block bg-drelo-deal text-drelo-ink text-xs font-black px-2.5 py-1 rounded mb-2">{p.badge}</span>}
          <h1 className="font-display font-black text-2xl md:text-4xl tracking-tight leading-tight" data-testid="product-title">{p.title}</h1>

          {p.reviews_count > 0 && (
            <div className="flex items-center gap-2 mt-3 text-sm">
              <div className="flex">{Array(5).fill(0).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.round(p.rating || 0) ? "fill-drelo-deal text-drelo-deal" : "text-slate-300"}`} />)}</div>
              {p.rating != null && <span className="font-bold">{p.rating}</span>}
              <span className="text-drelo-muted">· {p.reviews_count} {p.reviews_count === 1 ? "reseña" : "reseñas"}</span>
            </div>
          )}

          <div className="mt-4 bg-orange-50 rounded-xl p-4 border border-orange-100">
            <div className="flex items-end gap-3">
              <span className="font-display font-black text-4xl text-drelo-primary">${p.price.toFixed(2)}</span>
              {p.compare_at && <span className="text-lg text-drelo-muted line-through mb-1">${p.compare_at.toFixed(2)}</span>}
              {discount > 0 && <span className="bg-drelo-urgency text-white text-xs font-black px-2 py-1 rounded mb-1.5">AHORRAS ${(p.compare_at - p.price).toFixed(2)}</span>}
            </div>
            <div className="flex items-center gap-2 mt-2 text-sm font-bold text-drelo-urgency">
              <Flame className="w-4 h-4" /> Oferta termina en <Countdown hoursFromNow={5} testId="detail-countdown" />
            </div>
          </div>

          {typeof p.stock === "number" && p.stock <= 15 && (
            <div className="mt-3 text-sm font-bold text-drelo-urgency animate-drelo-pulse">⚠️ ¡Solo quedan {p.stock} en stock!</div>
          )}

          <p className="mt-4 text-drelo-muted leading-relaxed">{p.description}</p>

          {p.is_ingestible === true && (
            <div className="mt-4 text-xs text-drelo-muted border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid="fda-disclaimer">
              <p>These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.</p>
              <p className="mt-1">Estas afirmaciones no han sido evaluadas por la Administración de Alimentos y Medicamentos (FDA). Este producto no está destinado a diagnosticar, tratar, curar ni prevenir ninguna enfermedad.</p>
            </div>
          )}

          <div className="mt-5 flex items-center gap-4">
            <div className="flex items-center gap-3 bg-slate-100 rounded-full px-2 py-1.5">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-1.5" data-testid="qty-dec"><Minus className="w-4 h-4" /></button>
              <span className="font-bold w-6 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-1.5" data-testid="qty-inc"><Plus className="w-4 h-4" /></button>
            </div>
            <span className="text-sm text-drelo-muted">Total: <span className="font-black text-drelo-ink">${(p.price * qty).toFixed(2)}</span></span>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <button onClick={addCart} className="border-2 border-drelo-primary text-drelo-primary font-black uppercase py-3.5 rounded-xl hover:bg-orange-50 active:scale-95 transition-all" data-testid="detail-add-cart">Añadir al carrito</button>
            <button onClick={buyNow} className="bg-drelo-primary text-white font-black uppercase py-3.5 rounded-xl hover:bg-drelo-hover active:scale-95 transition-all" data-testid="detail-buy-now">Comprar ahora</button>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2 text-center text-xs">
            {[[Truck, "Envío gratis +$35"], [ShieldCheck, "Pago seguro"], [RotateCcw, "Devolución 30 días"]].map(([Icon, t], i) => (
              <div key={i} className="bg-white border rounded-lg py-3 px-1"><Icon className="w-5 h-5 mx-auto mb-1 text-drelo-primary" /><div className="font-semibold">{t}</div></div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-12">
        <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">Reseñas de clientes</h2>
        <div className="bg-white border rounded-xl p-8 text-center text-drelo-muted">
          <p className="font-bold text-drelo-ink">Sé el primero en opinar</p>
          <p className="text-sm mt-1">Este producto aún no tiene reseñas. Compártelo después de tu compra.</p>
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">También te puede gustar</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {related.map((rp, i) => <ProductCard key={rp.id} product={rp} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
